import { useEffect, useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { Logo } from './Logo';
import { SocialIcons } from './SocialIcons';
import { NewsletterForm } from './NewsletterForm';
import { publicApi } from '@/lib/api';
import { NAV } from '@/constants/testIds';

const NAV_LINKS = [
  { to: '/', label: 'Home', end: true, key: 'home' },
  { to: '/season', label: 'The Season', key: 'season' },
  { to: '/field-notes', label: 'Field Notes', key: 'fieldNotes' },
  { to: '/recap', label: 'Recaps', key: 'recaps' },
  { to: '/about', label: 'About', key: 'about' },
  { to: '/newsletter', label: 'Newsletter', key: 'newsletter' },
  { to: '/contact', label: 'Contact', key: 'contact' },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium tracking-tight transition-colors ${
    isActive
      ? 'text-[hsl(var(--primary))]'
      : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
  }`;

export const PublicLayout = () => {
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    publicApi.siteConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setOpen(false);
  }, [location.pathname]);

  const links = [...NAV_LINKS];
  if (config?.gear_nav_enabled) {
    links.push({ to: '/gear', label: 'Gear', key: 'gear' });
  }

  return (
    <div className="min-h-screen flex flex-col bg-[hsl(var(--background))]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-[hsl(var(--primary))] focus:text-[hsl(var(--primary-foreground))] focus:px-3 focus:py-2 focus:text-sm focus:shadow"
      >
        Skip to content
      </a>
      <header
        className={`sticky top-0 z-40 w-full border-b transition-colors ${
          scrolled
            ? 'bg-[hsl(var(--background)/0.9)] backdrop-blur border-[hsl(var(--border))]'
            : 'bg-transparent border-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.end}
                className={linkClass}
                data-testid={NAV[l.key]}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/newsletter" className="hidden sm:inline-flex">
              <Button size="sm" className="h-9">Subscribe</Button>
            </Link>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Open menu"
                  data-testid={NAV.mobileToggle}
                >
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] sm:w-[380px]" data-testid={NAV.mobileSheet}>
                <SheetTitle className="font-serif text-xl">A Guy Who Hunts</SheetTitle>
                <SheetDescription className="text-xs">
                  Public land. Limited time. Honest results.
                </SheetDescription>
                <nav aria-label="Mobile primary" className="mt-6 flex flex-col gap-2">
                  {links.map((l) => (
                    <NavLink
                      key={l.key}
                      to={l.to}
                      end={l.end}
                      onClick={() => setOpen(false)}
                      data-testid={`${NAV[l.key]}-mobile`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-2.5 text-base font-medium transition-colors ${
                          isActive
                            ? 'bg-[hsl(var(--accent)/0.35)] text-[hsl(var(--foreground))]'
                            : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent)/0.2)] hover:text-[hsl(var(--foreground))]'
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-6 border-t pt-6">
                  <p className="kicker mb-3">Follow</p>
                  <SocialIcons config={config} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main id="main" className="flex-1">
        <Outlet context={{ config }} />
      </main>

      <footer className="mt-20 border-t border-[hsl(var(--border))] bg-[hsl(var(--secondary)/0.5)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Logo />
              <p className="mt-4 max-w-md text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                Public land. Limited time. Honest results. A documentary of one adult hunter
                learning the process, one honest season at a time.
              </p>
              <div className="mt-5">
                <SocialIcons config={config} />
              </div>
            </div>
            <div className="lg:col-span-4">
              <p className="kicker mb-3">The Field Report</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                Sent when there is something worth sharing.
              </p>
              <NewsletterForm variant="inline" />
            </div>
            <div className="lg:col-span-3">
              <p className="kicker mb-3">Site</p>
              <ul className="space-y-2 text-sm">
                {links.map((l) => (
                  <li key={l.key}>
                    <Link
                      to={l.to}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/privacy"
                    className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/affiliate-disclosure"
                    className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  >
                    Affiliate disclosure
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-col gap-2 border-t border-[hsl(var(--border))] pt-6 text-xs text-[hsl(var(--muted-foreground))] sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} A Guy Who Hunts. All rights reserved.</p>
            <p>{config?.contact_email || 'hello@aguywhohunts.com'}</p>
          </div>
        </div>
      </footer>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
};
